import { Dispatch, SetStateAction } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Box, InputPassword, Typography } from '@lyearn/ui';
import PasswordWithTooltip from '@/modules/auth/components/PasswordWithTooltip';

import { placeholderTranslation, TitleMap, titleTranslation } from './utils';

type Props = {
  isDirty: Record<keyof typeof TitleMap, boolean>;
  passwords: Record<keyof typeof TitleMap, string>;
  errors: Record<keyof typeof TitleMap, string>;
  setPasswords: Dispatch<SetStateAction<Record<keyof typeof TitleMap, string>>>;
  setIsDirty: Dispatch<SetStateAction<Record<keyof typeof TitleMap, boolean>>>;
  isSubmitBtnClicked: boolean;
};

const PasswordInputs = (props: Props) => {
  const { t } = useTranslation();
  const { isDirty, setIsDirty, passwords, setPasswords, errors, isSubmitBtnClicked } = props;
  const isCurrentPasswordError =
    !!errors[TitleMap.currentPassword] && (isDirty[TitleMap.currentPassword] || isSubmitBtnClicked);

  return (
    <>
      <Box key={TitleMap.currentPassword} className="mb-24">
        <Typography className="mb-8 ms-8" variant="body-short-02">
          {t(titleTranslation[TitleMap.currentPassword])}
        </Typography>
        <InputPassword
          fullWidth
          error={isCurrentPasswordError}
          margin="dense"
          placeholder={t(placeholderTranslation[TitleMap.currentPassword])}
          size="large"
          onBlur={() =>
            setIsDirty((prevIsDirty) => ({ ...prevIsDirty, [TitleMap.currentPassword]: true }))
          }
          onChangeText={(text) => {
            setPasswords((prevPasswords) => ({
              ...prevPasswords,
              [TitleMap.currentPassword]: text,
            }));
          }}
        />
        {isCurrentPasswordError ? (
          <Typography className="mt-8 ms-8" color="text-text-error" variant="body-short-03">
            {errors[TitleMap.currentPassword]}
          </Typography>
        ) : null}
      </Box>
      {[TitleMap.newPassword, TitleMap.confirmPassword].map((key: keyof typeof passwords) => {
        const showError = !!errors[key] && (isDirty[key] || isSubmitBtnClicked);
        return (
          <Box key={key} className="mb-24">
            <Typography className="mb-8 ms-8" variant="body-short-02">
              {t(titleTranslation[key])}
            </Typography>
            <PasswordWithTooltip
              errorMessage={errors[key]}
              isError={showError}
              password={passwords[key]}
              placeholder={t(placeholderTranslation[key])}
              onBlur={() => setIsDirty((prevIsDirty) => ({ ...prevIsDirty, [key]: true }))}
              onChangeText={(text) => {
                setPasswords((prevPasswords) => ({ ...prevPasswords, [key]: text }));
              }}
            />
          </Box>
        );
      })}
    </>
  );
};

export default PasswordInputs;
